import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { jobAPI, resumeAPI } from '../../services/api';
import ApiService from '../../services/api';
import RichTextEditor from './RichTextEditor';
import { getJobPosterLabel, getJobPosterUsername, shouldLinkJobPoster, getJobCompanyNameForMeta } from '../../utils/jobDisplay';
import './JobApplicationForm.css';

const JobApplicationForm = ({ job, isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState({
    cover_letter: '',
    resume_id: null,
    use_remotebirdy_resume: false,
    meta_responses: {},
  });
  const [resumes, setResumes] = useState([]);
  const [userSkills, setUserSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resumesLoading, setResumesLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [coverLetterGenerated, setCoverLetterGenerated] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (isOpen && user) {
      fetchUserResumes();
      fetchUserSkills();
      generateCoverLetter();
      // Initialize meta_responses based on job's meta_fields
      if (job?.meta_fields && Array.isArray(job.meta_fields)) {
        const initialResponses = {};
        job.meta_fields.forEach((field) => {
          if (field.type === 'checkbox') {
            initialResponses[field.id] = [];
          } else {
            initialResponses[field.id] = '';
          }
        });
        setFormData(prev => ({
          ...prev,
          meta_responses: initialResponses
        }));
      }
    }
  }, [isOpen, user, job]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const fetchUserResumes = async () => {
    setResumesLoading(true);
    try {
      const response = await resumeAPI.getAll();
      if (response.success) {
        setResumes(response.data || []);
        // Set default resume if available
        if (response.default_resume) {
          setFormData(prev => ({
            ...prev,
            resume_id: response.default_resume,
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching resumes:', error);
      setResumes([]);
    } finally {
      setResumesLoading(false);
    }
  };

  const fetchUserSkills = async () => {
    try {
      const response = await ApiService.get('/user-skills');
      if (response.success) {
        setUserSkills(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching user skills:', error);
      setUserSkills([]);
    }
  };

  const generateCoverLetter = () => {
    if (!user || coverLetterGenerated) return;

    const bio = user.bio || '';
    const skills = userSkills.map(skill => skill.skill?.name || skill.name || '').filter(Boolean);
    const skillsList = skills.length > 0 ? skills.join(', ') : 'various technical and professional skills';
    
    // Get user's professional title if available
    const professionalTitle = user.professional_title || 'professional';
    const userName = user.name || 'I';

    // Generate a personalized cover letter
    let coverLetter = `Dear Hiring Manager,\n\n`;
    
    if (bio) {
      coverLetter += `I am writing to express my interest in the ${job.title} position at ${getJobCompanyNameForMeta(job)}. `;
      coverLetter += `${bio}\n\n`;
    } else {
      coverLetter += `I am writing to express my strong interest in the ${job.title} position at ${getJobCompanyNameForMeta(job)}. `;
      coverLetter += `As a ${professionalTitle}, I am excited about the opportunity to contribute to your team.\n\n`;
    }

    // Add skills section
    if (skills.length > 0) {
      coverLetter += `My expertise includes ${skillsList}. `;
    }

    // Add why they're a good fit
    coverLetter += `I am particularly drawn to this role because it aligns perfectly with my experience and career goals. `;
    
    if (job.skills_required && Array.isArray(job.skills_required) && job.skills_required.length > 0) {
      const matchingSkills = job.skills_required.filter(skill => 
        skills.some(userSkill => 
          userSkill.toLowerCase().includes(skill.toLowerCase()) || 
          skill.toLowerCase().includes(userSkill.toLowerCase())
        )
      );
      
      if (matchingSkills.length > 0) {
        coverLetter += `I have extensive experience with ${matchingSkills.slice(0, 3).join(', ')}, which are key requirements for this position. `;
      }
    }

    coverLetter += `I am confident that my skills and passion for excellence would make me a valuable addition to your team.\n\n`;
    coverLetter += `Thank you for considering my application. I look forward to the opportunity to discuss how my background and expertise can contribute to ${getJobCompanyNameForMeta(job)}'s continued success.\n\n`;
    coverLetter += `Sincerely,\n${userName}`;

    setFormData(prev => ({
      ...prev,
      cover_letter: coverLetter,
    }));
    setCoverLetterGenerated(true);
  };

  if (!isOpen || !mounted) return null;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleResumeChange = (e) => {
    const value = e.target.value;
    if (value === 'remotebirdy') {
      setFormData(prev => ({
        ...prev,
        resume_id: null,
        use_remotebirdy_resume: true,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        resume_id: value ? parseInt(value) : null,
        use_remotebirdy_resume: false,
      }));
    }
  };

  const handleMetaResponseChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      meta_responses: {
        ...prev.meta_responses,
        [fieldId]: value
      }
    }));
    // Clear error for this field
    if (errors[`meta_${fieldId}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`meta_${fieldId}`];
        return newErrors;
      });
    }
  };

  const handleMetaCheckboxChange = (fieldId, optionValue, checked) => {
    setFormData(prev => {
      const currentResponses = prev.meta_responses[fieldId] || [];
      const updatedResponses = checked
        ? [...currentResponses, optionValue]
        : currentResponses.filter(v => v !== optionValue);
      
      return {
        ...prev,
        meta_responses: {
          ...prev.meta_responses,
          [fieldId]: updatedResponses
        }
      };
    });
    // Clear error for this field
    if (errors[`meta_${fieldId}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`meta_${fieldId}`];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Validation
    const newErrors = {};
    if (!formData.cover_letter.trim()) {
      newErrors.cover_letter = 'Cover letter is required';
    } else if (formData.cover_letter.trim().length < 50) {
      newErrors.cover_letter = 'Cover letter must be at least 50 characters';
    }

    // Resume validation - either uploaded resume or RemoteBirdy resume
    if (!formData.resume_id && !formData.use_remotebirdy_resume) {
      newErrors.resume = 'Please select a resume to submit with your application';
    }

    // Validate meta fields (custom questions)
    if (job?.meta_fields && Array.isArray(job.meta_fields)) {
      job.meta_fields.forEach((field) => {
        if (field.required) {
          const response = formData.meta_responses[field.id];
          if (field.type === 'checkbox') {
            if (!response || !Array.isArray(response) || response.length === 0) {
              newErrors[`meta_${field.id}`] = 'This question is required';
            }
          } else {
            if (!response || (typeof response === 'string' && !response.trim())) {
              newErrors[`meta_${field.id}`] = 'This question is required';
            }
          }
        }
      });
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);
    try {
      const applicationData = {
        cover_letter: formData.cover_letter.trim(),
        meta_responses: formData.meta_responses,
      };

      // Add resume_id if using uploaded resume, or use_remotebirdy_resume flag
      if (formData.use_remotebirdy_resume) {
        applicationData.use_remotebirdy_resume = true;
      } else if (formData.resume_id) {
        applicationData.resume_id = formData.resume_id;
      }

      const response = await jobAPI.apply(job.id, applicationData);

      if (response.success) {
        if (onSuccess) {
          onSuccess(response.data);
        }
        onClose();
        // Reset form
        setFormData({
          cover_letter: '',
          resume_id: null,
          use_remotebirdy_resume: false,
          meta_responses: {},
        });
        setCoverLetterGenerated(false);
      } else {
        // Check if user has already applied
        if (response.message && response.message.toLowerCase().includes('already applied')) {
          setErrors({ general: 'You have already applied for this job.' });
          // Close form after a delay to show the message
          setTimeout(() => {
            onClose();
            if (onSuccess) {
              onSuccess(null); // Trigger update to show "Applied" state
            }
          }, 2000);
        } else {
          if (response.errors) {
            setErrors(response.errors);
          } else {
            setErrors({ general: response.message || 'Failed to submit application' });
          }
        }
      }
    } catch (error) {
      console.error('Error applying to job:', error);
      setErrors({ general: error.message || 'An error occurred while submitting your application' });
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="job-application-modal-overlay" onClick={onClose}>
      <div className="job-application-modal" onClick={(e) => e.stopPropagation()}>
        <div className="job-application-header">
          <div className="header-left">
            <h2>Apply for {job.title}</h2>
            {(() => {
              const label = getJobPosterLabel(job);
              const posterUsername = getJobPosterUsername(job);
              const linkPoster = shouldLinkJobPoster(job);

              if (linkPoster && posterUsername) {
                return (
                  <a href={`/talent/${posterUsername}`} target="_blank" rel="noopener noreferrer" className="header-subtitle header-subtitle-link">{label}</a>
                );
              }

              return <p className="header-subtitle">{label}</p>;
            })()}
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="job-application-form">
          {errors.general && (
            <div className="error-message general-error">
              {errors.general}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="cover_letter">
              Cover Letter <span className="required">*</span>
            </label>
            <textarea
              id="cover_letter"
              name="cover_letter"
              value={formData.cover_letter}
              onChange={handleInputChange}
              className={errors.cover_letter ? 'error' : ''}
              placeholder="Tell us why you're a great fit for this position..."
              rows="10"
              disabled={loading}
            />
            <div className="char-count">
              {formData.cover_letter.length} characters {formData.cover_letter.length < 50 && '(minimum 50 required)'}
            </div>
            {errors.cover_letter && <span className="error-text">{errors.cover_letter}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="resume">
              Select Resume <span className="required">*</span>
            </label>
            {resumesLoading ? (
              <div className="resume-loading">Loading resumes...</div>
            ) : (
              <select
                id="resume"
                name="resume"
                value={formData.use_remotebirdy_resume ? 'remotebirdy' : (formData.resume_id || '')}
                onChange={handleResumeChange}
                className={errors.resume ? 'error' : ''}
                disabled={loading}
              >
                <option value="">-- Select a resume --</option>
                <option value="remotebirdy">Use RemoteBirdy Resume</option>
                {resumes.map((resume) => (
                  <option key={resume.id} value={resume.id}>
                    {resume.name || resume.original_name || `Resume ${resume.id}`}
                    {resume.is_default && ' (Default)'}
                  </option>
                ))}
              </select>
            )}
            {errors.resume && <span className="error-text">{errors.resume}</span>}
            <div className="resume-help-text">
              {formData.use_remotebirdy_resume 
                ? 'Your RemoteBirdy profile will be used as your resume'
                : formData.resume_id 
                  ? `Selected: ${resumes.find(r => r.id === formData.resume_id)?.name || resumes.find(r => r.id === formData.resume_id)?.original_name || 'Resume'}`
                  : 'Choose an uploaded resume or use your RemoteBirdy profile'}
            </div>
          </div>

          {/* Custom Questions */}
          {job?.meta_fields && Array.isArray(job.meta_fields) && job.meta_fields.length > 0 && (
            <div className="form-group" style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: '#111827' }}>
                Additional Questions
              </h3>
              {job.meta_fields.map((field) => (
                <div key={field.id} style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    {field.question}
                    {field.required && <span className="required"> *</span>}
                  </label>

                  {field.type === 'text' && (
                    <input
                      type="text"
                      value={formData.meta_responses[field.id] || ''}
                      onChange={(e) => handleMetaResponseChange(field.id, e.target.value)}
                      disabled={loading}
                      className={errors[`meta_${field.id}`] ? 'error' : ''}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  )}

                  {field.type === 'textarea' && (
                    <textarea
                      value={formData.meta_responses[field.id] || ''}
                      onChange={(e) => handleMetaResponseChange(field.id, e.target.value)}
                      disabled={loading}
                      rows={5}
                      className={errors[`meta_${field.id}`] ? 'error' : ''}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                  )}

                  {field.type === 'rich_text' && (
                    <div>
                      <RichTextEditor
                        value={formData.meta_responses[field.id] || ''}
                        onChange={(content) => handleMetaResponseChange(field.id, content)}
                        placeholder="Enter your answer..."
                        disabled={loading}
                        error={errors[`meta_${field.id}`]}
                      />
                    </div>
                  )}

                  {field.type === 'dropdown' && (
                    <select
                      value={formData.meta_responses[field.id] || ''}
                      onChange={(e) => handleMetaResponseChange(field.id, e.target.value)}
                      disabled={loading}
                      className={errors[`meta_${field.id}`] ? 'error' : ''}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">-- Select an option --</option>
                      {field.options && field.options.map((option) => (
                        <option key={option.id} value={option.option_text || option.optionText}>
                          {option.option_text || option.optionText}
                        </option>
                      ))}
                    </select>
                  )}

                  {field.type === 'checkbox' && (
                    <div>
                      {field.options && field.options.map((option) => {
                        const optionText = option.option_text || option.optionText;
                        return (
                          <div key={option.id} style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              id={`meta_${field.id}_option_${option.id}`}
                              checked={(formData.meta_responses[field.id] || []).includes(optionText)}
                              onChange={(e) => handleMetaCheckboxChange(field.id, optionText, e.target.checked)}
                              disabled={loading}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <label 
                              htmlFor={`meta_${field.id}_option_${option.id}`}
                              style={{ fontSize: '14px', cursor: 'pointer', margin: 0 }}
                            >
                              {optionText}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {field.type === 'radio' && (
                    <div>
                      {field.options && field.options.map((option) => {
                        const optionText = option.option_text || option.optionText;
                        return (
                          <div key={option.id} style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              id={`meta_${field.id}_option_${option.id}`}
                              name={`meta_${field.id}`}
                              value={optionText}
                              checked={formData.meta_responses[field.id] === optionText}
                              onChange={(e) => handleMetaResponseChange(field.id, e.target.value)}
                              disabled={loading}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <label 
                              htmlFor={`meta_${field.id}_option_${option.id}`}
                              style={{ fontSize: '14px', cursor: 'pointer', margin: 0 }}
                            >
                              {optionText}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {errors[`meta_${field.id}`] && (
                    <span className="error-text" style={{ display: 'block', marginTop: '4px' }}>
                      {errors[`meta_${field.id}`]}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default JobApplicationForm;
